import { FC, useCallback, useRef, useState } from 'react'

import { fetchQuery } from '@sequencehq/api'
import {
  dashboard20240730Client,
  DashboardApi20240730
} from '@sequencehq/api/dashboard/v20240730'
import { IntegrationServices } from '@sequencehq/api/utils/commonEnums'
import {
  AsyncInputSelectControlField,
  Center,
  Flex,
  GreyGrey60,
  Primary13Regular
} from '@sequencehq/core-components'
import { MINUTES } from '@sequencehq/utils/dates'
import { apiQueryClient } from 'features/api/apiQueryClient'
import { match } from 'ts-pattern'
import { ExternalAccountDetails } from './LinkCustomerModal/ExternalAccountDetails'
import { integrationCustomerName } from './integrationCustomerName'

type IntegrationCustomer =
  DashboardApi20240730.GetIntegrationCustomersService.IntegrationCustomer

/**
 * The connector search rejects a shorter query, and reads an empty one as "give
 * me everything" - which is the slow call this typeahead exists to avoid. So we
 * don't ask until the user has typed this much.
 */
const MIN_SEARCH_LENGTH = 2

/** Anything longer is rejected by the connector search, so trim it down first. */
const MAX_SEARCH_LENGTH = 200

/** What the last finished search turned up, so we can say why there are no rows. */
type SearchOutcome = {
  query: string
  matches: number
  unlinkedMatches: number
}

/**
 * Picks a customer on a connector that can search its own customers, so we never
 * pull the account's whole customer list down to the browser.
 */
export const IntegrationCustomerTypeahead: FC<{
  service: IntegrationServices
  serviceName: string
  value: string
  onChange: (newValue: string) => void
  label?: string
  'data-testid': string
}> = ({
  service,
  serviceName,
  value,
  onChange,
  label,
  'data-testid': dataTestId
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [lastSearch, setLastSearch] = useState<SearchOutcome | undefined>(
    undefined
  )
  const [selectedCustomer, setSelectedCustomer] = useState<
    IntegrationCustomer | undefined
  >(undefined)

  /**
   * The select only hands us back the id of the customer the user picked, so
   * keep every customer a search has turned up. We need the rest of the picked
   * one - its name and link - to show its details underneath.
   */
  const customersById = useRef<Map<string, IntegrationCustomer>>(new Map())

  const fetcher = useCallback(
    async (term: string): Promise<IntegrationCustomer[]> => {
      const query = term.trim().slice(0, MAX_SEARCH_LENGTH)

      if (query.length < MIN_SEARCH_LENGTH) {
        return []
      }

      const data = await fetchQuery(
        apiQueryClient,
        dashboard20240730Client.getIntegrationCustomersService,
        { service, queryParams: { query } },
        { staleTime: 1 * MINUTES }
      )

      const items = data?.items ?? []

      customersById.current = new Map([
        ...customersById.current,
        ...items.map(item => [item.id, item] as const)
      ])

      setLastSearch({
        query,
        matches: items.length,
        unlinkedMatches: items.filter(item => !item.isLinked).length
      })

      return items
    },
    [service]
  )

  const mapToOption = useCallback(
    (customer: IntegrationCustomer) =>
      customer.isLinked
        ? null
        : { label: integrationCustomerName(customer), value: customer.id },
    []
  )

  const handleChange = useCallback(
    (newValue: string) => {
      setSelectedCustomer(customersById.current.get(newValue))
      setSearchTerm('')
      onChange(newValue)
    },
    [onChange]
  )

  const query = searchTerm.trim().slice(0, MAX_SEARCH_LENGTH)

  const emptyStateMessage = match({
    canSearch: query.length >= MIN_SEARCH_LENGTH,
    hasFinishedSearching: lastSearch?.query === query,
    everyMatchLinked: Boolean(
      lastSearch && lastSearch.matches > 0 && lastSearch.unlinkedMatches === 0
    )
  })
    .with(
      { canSearch: false },
      () => `Type at least ${MIN_SEARCH_LENGTH} characters to search`
    )
    .with({ hasFinishedSearching: false }, () => `Searching ${serviceName}...`)
    .with(
      { everyMatchLinked: true },
      () => `Every matching ${serviceName} customer is already linked`
    )
    .otherwise(() => `No ${serviceName} customers found`)

  return (
    <Flex flexDirection="column" gap="16px">
      <AsyncInputSelectControlField
        label={label}
        data-testid={dataTestId}
        fetcher={fetcher}
        mapToOption={mapToOption}
        value={value}
        onChange={handleChange}
        onSearch={setSearchTerm}
        onToggle={isOpen => {
          // Closing throws the results away, so forget what was typed too -
          // otherwise reopening reads as "no results" for a search we no
          // longer hold.
          if (!isOpen) {
            setSearchTerm('')
          }
        }}
        skipInitialFetch
        placeholder={`Search for a ${serviceName} customer`}
        emptyState={
          <Center padding="16px" color={GreyGrey60} {...Primary13Regular}>
            {emptyStateMessage}
          </Center>
        }
      />

      {selectedCustomer && (
        <ExternalAccountDetails
          name={integrationCustomerName(selectedCustomer)}
          externalUrl={selectedCustomer.externalUrl}
          serviceName={serviceName}
        />
      )}
    </Flex>
  )
}
